import { webSocket, WebSocketSubject } from "rxjs/webSocket";
import { Subject, Subscription, Observable, map, merge, filter, tap, interval, takeWhile } from "rxjs";
import { Channel } from "./channel";
import {
  IncomingMessages,
  isIncomingMessage,
  OutgoingMessages,
  InChannelMessages,
  OutChannelMessages,
} from "./message";

export type PubSubClientOptions = {
  host: string;
  appId: string;
};

const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30000;

export class PubSubClient {
  private socket: WebSocketSubject<IncomingMessages | OutgoingMessages>;
  public readonly connect$: Observable<boolean>;
  public readonly close$: Observable<Event>;
  protected readonly messages$ = new Subject<unknown>();
  private readonly channelInStream: Observable<InChannelMessages>;
  private open: boolean = false;
  private disposed: boolean = false;
  private reconnectAttempt: number = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingSub: Subscription | null = null;
  private channelOutStream = new Subject<OutChannelMessages>();
  private readonly joinedChannels = new Map<string, string | undefined>();
  private readonly connectSubject = new Subject<Event>();
  private readonly closeSubject = new Subject<Event>();

  constructor(private readonly options: PubSubClientOptions) {
    if (typeof window === "undefined") {
      throw new Error("This client is only supported in the browser");
    }

    this.socket = this.createSocket();

    this.channelInStream = this.messages$
      .asObservable()
      .pipe(filter((s): s is InChannelMessages => isIncomingMessage(s) && s.service === "channel"));

    this.connectSubject.subscribe(() => {
      this.open = true;
      this.reconnectAttempt = 0;
      this.pingSub?.unsubscribe();
      this.pingSub = interval(30000)
        .pipe(takeWhile(() => this.open && !this.disposed))
        .subscribe(() => {
          this.socket.next({ service: "root", action: "ping" });
        });
      this.rejoinChannels();
    });

    this.closeSubject.subscribe(() => {
      this.open = false;
    });

    this.subscribeToSocket();
    this.connect$ = merge(this.connectSubject, this.closeSubject).pipe(map((event) => event.type === "open"));
    this.close$ = this.closeSubject.pipe(tap(() => {}));
    this.channelOutStream.subscribe({
      next: (m) => {
        if (m.action === "leave" && "channel" in m.data) {
          this.joinedChannels.delete((m.data as { channel: string }).channel);
        }
        this.socket.next({
          ...m,
          appId: this.options.appId,
        });
      },
    });
  }

  private createSocket(): WebSocketSubject<IncomingMessages | OutgoingMessages> {
    return webSocket({
      url: `${this.options.host}`,
      openObserver: this.connectSubject,
      closeObserver: this.closeSubject,
    });
  }

  private subscribeToSocket(): void {
    this.socket.subscribe({
      next: (message) => {
        this.messages$.next(message);
      },
      error: () => {
        this.scheduleReconnect();
      },
      complete: () => {
        if (!this.disposed) {
          this.scheduleReconnect();
        }
      },
    });
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

    this.open = false;
    const jitter = Math.random() * RECONNECT_BASE_DELAY;
    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempt) + jitter,
      RECONNECT_MAX_DELAY,
    );
    this.reconnectAttempt++;

    this.reconnectTimer = setTimeout(() => {
      if (this.disposed) return;
      this.socket = this.createSocket();
      this.subscribeToSocket();
    }, delay);
  }

  private rejoinChannels(): void {
    this.joinedChannels.forEach((token, channel) => {
      this.socket.next({
        data: { channel },
        service: "channel",
        token: token,
        appId: this.options.appId,
        action: "join",
      });
    });
  }

  public join = (channel: string, token: string | undefined = undefined): Channel => {
    this.joinedChannels.set(channel, token);

    this.socket.next({
      data: { channel },
      service: "channel",
      token: token,
      appId: this.options.appId,
      action: "join",
    });

    return new Channel(
      channel,
      this.options.appId,
      this.channelInStream.pipe(filter((m) => m.data.channel === channel)),
      this.channelOutStream,
    );
  };

  public leave = (channel: Channel): void => {
    this.joinedChannels.delete(channel.name);

    this.socket.next({
      data: { channel: channel.name },
      service: "channel",
      appId: this.options.appId,
      action: "leave",
    });
  };

  public disconnect = (): void => {
    this.disposed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.socket.complete();
    this.messages$.complete();
  };
}
